import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
    ADDITIONAL_SERVICE_PRODUCT_TYPE_SCHEMA,
    ADDITIONAL_SERVICE_SUPPORTED_UNITS_SCHEMA,
} from '@schuettflix/interfaces';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { AdditionalServiceRow } from '../components/AdditionalServiceRow.tsx';
import { Fieldset } from '../components/Fieldset.tsx';
import { useAutomaticFormContext } from '@/shared/forms/AutomaticProductsForm/context/AutomaticFormContext.tsx';
import { EXCLUDED_PARTNER_ORGANIZATION_IDS } from '@/shared/constants/excludedPartnerOrganizations.ts';
import { PlusIcon } from '@schuettflix/icons-react';
import { Button } from '@schuettflix/planum-react';
import { MARKET_CODE_TO_CURRENCY_CODE_MAP } from '@schuettflix/utils';
import { useMarketContext } from '@/shared/context/MarketContext.tsx';
import { AutomaticProductsFormAdditionalService } from '@/shared/forms/AutomaticProductsForm/schema/getAdditionalServiceSchema.ts';
import { Nullable } from '@/shared/types/Nullable.ts';
import { useAdditionalServiceQuantities } from '../hooks/useAdditionalServiceQuantities.ts';
import { toAdditionalServiceUnit } from '../utils/toAdditionalServiceUnit.ts';

export function AdditionalServicesFieldset() {
    const { t } = useTranslation();
    const { marketCode } = useMarketContext();
    const { taxClassOptions, customerOrganization, formRules, defaultTaxClass, defaultValuesLabels } =
        useAutomaticFormContext();
    const methods = useFormContext<AutomaticProductsFormValues>();
    const {
        register,
        control,
        formState: { defaultValues },
    } = methods;
    const { fields, append, remove } = useFieldArray({ control, name: 'additionalServices' });
    const { onSalesUnitChange, onPurchaseUnitChange, getDefaultQuantitiesForNewRow } =
        useAdditionalServiceQuantities(methods);

    return (
        <Fieldset
            title={t('forms.automaticProductsForm.sections.additionalServices')}
            className="mt-4"
            dataTest="additional-services-fieldset"
        >
            <div className="border-t-divider flex flex-col gap-4 border-t pt-4">
                {fields.map((field, index) => {
                    const defaultPartnerOrganizationId =
                        defaultValues?.additionalServices?.[index]?.partnerOrganizationId;

                    const disabledSalesUnit =
                        !!field.identifier && !!formRules?.additionalServices?.[field.identifier]?.salesUnit?.disabled;

                    return (
                        <AdditionalServiceRow
                            key={field.id}
                            productTypeProps={{
                                ...register(`additionalServices.${index}.productType`),
                                productTypes: [...ADDITIONAL_SERVICE_PRODUCT_TYPE_SCHEMA.options],
                            }}
                            productNameProps={{ ...register(`additionalServices.${index}.productName`) }}
                            currencyCodeProps={{ ...register(`additionalServices.${index}.currencyCode`) }}
                            partnerSelectionProps={{
                                ...register(`additionalServices.${index}.partnerOrganizationId`),
                                exclude: { ids: [...EXCLUDED_PARTNER_ORGANIZATION_IDS, customerOrganization.id] },
                                defaultValue: defaultPartnerOrganizationId,
                                defaultValueLabel:
                                    defaultPartnerOrganizationId !== undefined
                                        ? defaultValuesLabels?.additionalServices?.[defaultPartnerOrganizationId]
                                        : undefined,
                            }}
                            salesQuantityProps={{ ...register(`additionalServices.${index}.salesQuantity`) }}
                            salesUnitProps={{
                                ...register(`additionalServices.${index}.salesUnit`),
                                disabled: disabledSalesUnit,
                                supportedUnitNames: [...ADDITIONAL_SERVICE_SUPPORTED_UNITS_SCHEMA.options],
                                onUnitChange: unit => onSalesUnitChange(index, toAdditionalServiceUnit(unit)),
                            }}
                            salesPriceProps={{ ...register(`additionalServices.${index}.salesPrice`) }}
                            salesTaxClassIdProps={{
                                ...register(`additionalServices.${index}.salesTaxClassId`),
                                options: taxClassOptions,
                            }}
                            purchaseQuantityProps={{ ...register(`additionalServices.${index}.purchaseQuantity`) }}
                            purchaseUnitProps={{
                                ...register(`additionalServices.${index}.purchaseUnit`),
                                supportedUnitNames: [...ADDITIONAL_SERVICE_SUPPORTED_UNITS_SCHEMA.options],
                                onUnitChange: unit => onPurchaseUnitChange(index, toAdditionalServiceUnit(unit)),
                            }}
                            purchasePriceProps={{ ...register(`additionalServices.${index}.purchasePrice`) }}
                            purchaseTaxClassIdProps={{
                                ...register(`additionalServices.${index}.purchaseTaxClassId`),
                                options: taxClassOptions,
                            }}
                            showZeroPurchasePriceProps={{
                                ...register(`additionalServices.${index}.showZeroPurchasePrice`),
                            }}
                            showZeroSalesPriceProps={{ ...register(`additionalServices.${index}.showZeroSalesPrice`) }}
                            dataTest={`additional-service-${index}`}
                            onDelete={() => remove(index)}
                            deleteDisabled={
                                field.identifier !== undefined &&
                                formRules?.additionalServices?.[field.identifier]?.disableDelete
                            }
                        />
                    );
                })}
            </div>

            <Button
                type="button"
                size="md"
                variant="tertiary"
                className="self-start !pl-0"
                onClick={() => {
                    const { salesQuantity, purchaseQuantity } = getDefaultQuantitiesForNewRow({
                        salesUnit: 'PIECE',
                        purchaseUnit: 'PIECE',
                    });

                    append({
                        currencyCode: MARKET_CODE_TO_CURRENCY_CODE_MAP[marketCode],
                        salesUnit: 'PIECE',
                        purchaseUnit: 'PIECE',
                        salesTaxClassId: defaultTaxClass,
                        purchaseTaxClassId: defaultTaxClass,
                        showZeroPurchasePrice: false,
                        showZeroSalesPrice: false,
                        // NOTE: RHF v7.54.2 doesn't leave a field empty when append() gets `undefined`
                        // for a key — it falls back to a stale value (e.g. leaks the previous row's default).
                        // Passing `null` avoids that, since RHF treats it as an explicit value.
                        partnerOrganizationId: null,
                        productType: null,
                        productName: null,
                        salesQuantity: salesQuantity ?? null,
                        purchaseQuantity: purchaseQuantity ?? null,
                        salesPrice: null,
                        purchasePrice: null,
                        // `unknown` bypasses the structural check for this assertion — AutomaticProductsFormAdditionalService's
                        // fields reject `null`, but that's the exact shape RHF's stale-default limitation (above) requires until the row is populated.
                    } satisfies Nullable<
                        Partial<AutomaticProductsFormAdditionalService>
                    > as unknown as Partial<AutomaticProductsFormAdditionalService>);
                }}
            >
                <PlusIcon size="xs" />
                {t('forms.automaticProductsForm.addAdditionalService')}
            </Button>
        </Fieldset>
    );
}
